import { logger } from './log.service';
import { copyToClipboard, getLocale } from '.';
import { EventBus } from '@/helpers/event-bus';
import { messageService } from '@/helpers/message.service';
import { isAuthenticated } from '@/helpers/auth-helper';
import { throwCancelled } from '@/helpers/error-helper';
import { RecaptchaResult } from '@/models/recaptcha.models';

class RecaptchaRequestQueue {
  private readonly _requests: Array<(e: RecaptchaResult) => void> = [];

  constructor() {
    EventBus.$on('recaptcha-token-response', this._handleRecaptchaResponse.bind(this));
  }

  add(resolver: (e: RecaptchaResult) => void): void {
    this._requests.push(resolver);
    if (this._requests.length === 1) {
      EventBus.$emit('recaptcha-token-request');
    }
  }

  private _handleRecaptchaResponse(result: RecaptchaResult): void {
    let resolver = this._requests.shift();

    if (resolver) resolver(result);

    if (this._requests.length > 0) {
      EventBus.$emit('recaptcha-token-request');
    }
  }
}

const recaptchaRequestQueue: RecaptchaRequestQueue = new RecaptchaRequestQueue();

export async function getRecaptchaToken(): Promise<RecaptchaResult> {
  return new Promise((resolve) => {
    let onGetResult = (result: RecaptchaResult) => {
      logger.debug('[RecaptchaHelper:getRecaptchaToken] result', result);
      if (result.isError) {
        const errorMsg = result.errorDescription || getLocale('appRecaptcha.recaptchaVerificationError').toString();
        const appUri = location.origin;
        const copyUriFuncName = 'copyOriginToClipboard';
        window[copyUriFuncName] = (e: PointerEvent) => {
          if (!e) return;

          e.stopPropagation();
          e.preventDefault();

          copyToClipboard(appUri)
            .then(() => {
              EventBus.$emit('show-tooltip', {
                message: getLocale('common.copiedUrl'),
                x: e.clientX,
                y: e.clientY
              });
            })
            .catch(err => logger.error(err));

          return false;
        };
        messageService.error({
          message: getLocale('appRecaptcha.recaptchaVerificationErrorExt', { errorMsg, appUri, copyUriFuncName }).toString()
        });
      }
      resolve(result);
    };
    recaptchaRequestQueue.add(onGetResult);
  });
}

export async function getRecaptchaTokenIfNotAuthenticated(): Promise<{recaptchaToken: string, recaptchaType: string}> {
  let recaptchaToken;
  let recaptchaType;
  if (!isAuthenticated()) {
    const recaptchaResult = await getRecaptchaToken();
    if (recaptchaResult.isCancelled) throwCancelled();
    if (recaptchaResult.isError) throw new Error(recaptchaResult.errorDescription);
    recaptchaToken = recaptchaResult.recaptchaToken;
    recaptchaType = recaptchaResult.recaptchaType;
  }
  return { recaptchaToken, recaptchaType };
}
